import dayjs from "dayjs";
import React, {useEffect, useState} from "react";
import {supabase} from "../api/supabase";
import {Ticker} from "../components";
import {useMedia} from "../hooks/useMedia";
import {useTotalRecipesSold} from "../hooks/useTotalRecipesSold";
import {hasSellingPlans} from "../lib/utils";
import {Trans, useTranslation} from "react-i18next";

interface useCTAContentProps {
  productJson: RootObject;
  selectedVariant: Variant;
}

function useCTAContent({productJson, selectedVariant}: useCTAContentProps) {
  const {t} = useTranslation();
  const isSubscription = hasSellingPlans(productJson);

  const isMobile = useMedia("(max-width: 768px)", true);

  const price = isSubscription
    ? productJson.compare_at_price / 100 / 2
    : selectedVariant.price / 100;

  const {total: orderedAmount} = useTotalRecipesSold({
    productId: productJson.id,
  });

  const [weekStartDate, setWeekStartDate] = useState<string | undefined>();
  const [now, setNow] = useState(dayjs());

  const minimumPrice = 4.23; //TODO: get from supabase

  const orderLockDateTime = dayjs(weekStartDate)
    .subtract(1, "day")
    .endOf("day");

  const duration = dayjs.duration(orderLockDateTime.diff(now));
  const daysUntilOrderLock = duration.days();

  useEffect(() => {
    if (isSubscription) {
      supabase
        .schema("airtablepg_production")
        .from("fct_recipe_weekly_availability")
        .select("week_start_day")
        .eq("recipe_shopify_id", productJson.id)
        .order("week_start_day")
        .limit(1)
        .then(
          ({data}) => {
            if (data && data.length > 0 && data[0]?.week_start_day) setWeekStartDate(data[0].week_start_day);
          },
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (daysUntilOrderLock > 3) return;
    const interval = setInterval(() => {
      setNow(dayjs());
    }, 1000);
    return () => clearInterval(interval);
  }, [weekStartDate, daysUntilOrderLock]);

  const isAvailableNextWeek =
    weekStartDate &&
    dayjs(weekStartDate).isAfter(now) &&
    dayjs(weekStartDate).isBefore(now.add(1, "week"));

  if (!isSubscription) {
    return {
      headline: (
        <h2 className="text-2xl leading-none mb-1 font-semibold tracking-wide whitespace-nowrap">
          {t("recipe_details.cta.versions.not_subscription.headline")}
        </h2>
      ),
      body: (
        <>
          <Trans
            i18nKey="recipe_details.cta.versions.not_subscription.body"
            values={{price: parseFloat(price.toFixed(2))}}
            components={{span: <span />, strong: <strong />}}
          />
        </>
      ),
      buttonLabel: t("recipe_details.cta.versions.not_subscription.button"),
      supportParagraph: t("recipe_details.cta.versions.not_subscription.support_paragraph"),
    };
  }

  if (!isAvailableNextWeek) {
    return {
      headline: (
        <h2 className="text-2xl leading-none mb-1 font-semibold tracking-wide whitespace-nowrap">
          {t("recipe_details.cta.versions.unavailable_next_week.headline")}
        </h2>
      ),
      body: (
          <Trans
            i18nKey="recipe_details.cta.versions.unavailable_next_week.body"
            values={{price: minimumPrice}}
            components={{
              spanMobile: <span className="md:hidden" />,
              spanDesktop: <span className="max-sm:hidden" />,
              span: <span />,
              strong: <strong />,
            }}
          />
      ),
      buttonLabel: isMobile ? t("recipe_details.cta.versions.unavailable_next_week.button_mobile") : t("recipe_details.cta.versions.unavailable_next_week.button_desktop"),
      supportParagraph: t("recipe_details.cta.versions.unavailable_next_week.support_paragraph"),
    };
  }

  if (daysUntilOrderLock !== undefined && daysUntilOrderLock < 3) {
    return {
      headline: (
        <h2 className="text-2xl leading-none mb-1 font-semibold tracking-wide whitespace-nowrap">
          <Trans i18nKey="recipe_details.cta.versions.locks_soon.headline" components={{span: <span className="max-md:hidden"/>}}/>
        </h2>
      ),
      body: (
        <span className="inline-flex items-center gap-1">
          <Trans
            i18nKey={"recipe_details.cta.versions.locks_soon.body"}
            components={{span: <span className="max-md:hidden"/>, ticker: <Ticker duration={duration}/>}}
          />
        </span>
      ),
      buttonLabel: isMobile ? t("recipe_details.cta.versions.locks_soon.button_mobile") : t("recipe_details.cta.versions.locks_soon.button_desktop"),
      supportParagraph: t("recipe_details.cta.versions.locks_soon.support_paragraph"),
      footNote: (
        <>
          <Trans
            i18nKey="recipe_details.cta.versions.locks_soon.foot_note"
            values={{price: price}}
            components={{strong: <strong />}}
          />
        </>
      ),
    };
  }

  return {
    headline: (
      <h1 className="text-3xl md:text-4xl leading-none md:-mt-1 font-semibold tracking-wide whitespace-nowrap">
        {t("recipe_details.cta.versions.available_next_week.title", {price: price})}
      </h1>
    ),
    body: t("recipe_details.cta.versions.available_next_week.body"),
    buttonLabel: isMobile ? t("recipe_details.cta.versions.available_next_week.button_mobile") : t("recipe_details.cta.versions.available_next_week.button_desktop"),
    supportParagraph: t("recipe_details.cta.versions.available_next_week.support_paragraph"),
    footNote: (
      <>
        <Trans
          i18nKey="recipe_details.cta.versions.available_next_week.foot_note"
          values={{orderedAmount: orderedAmount}}
          components={{strong: <strong />}}
        />
      </>
    ),
  };
}

export {useCTAContent};
